import { notifications } from '@mantine/notifications';
import { useState } from 'react';
import { BASE_URL } from '../config/api';
import { useTokenManagement } from '../hooks/useTokenManagement';
import { useVariablesStore, Variable } from '../hooks/useVariables';
import { ApiService } from '../services/api.service';
import { useEditorStore } from '../store/editorStore';

interface ThumbnailData {
  items: Array<{
    id: string;
    type: string;
    content: string;
    position: { x: number; y: number };
    size: { width: number; height: number };
    style: any;
    zIndex: number;
  }>;
  width: number;
  height: number;
  variables?: Array<{
    name: string;
    value: string;
  }>;
}

interface ThumbnailResponse {
  success: boolean;
  url: string;
  error?: string;
}

export const useThumbnailGenerator = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedThumbnail, setGeneratedThumbnail] = useState<string | null>(null);
  const [showResult, setShowResult] = useState(false);
  const items = useEditorStore((state) => state.items);
  const variables = useVariablesStore((state: { variables: Variable[] }) => state.variables);
  const { userToken, refreshUserToken } = useTokenManagement();

  const generateThumbnail = async () => {
    try {
      if (items.length === 0) {
        notifications.show({
          title: 'Không có phần tử',
          message: 'Thêm ít nhất một phần tử để tạo thumbnail',
          color: 'red'
        });
        return;
      }

      // Kiểm tra xem có đủ token không
      if (!userToken || userToken.balance < 500) {
        notifications.show({
          title: 'Không đủ token',
          message: 'Bạn cần ít nhất 500 token để tạo thumbnail',
          color: 'yellow',
        });
        return;
      }

      setIsGenerating(true);

      // Chuẩn bị dữ liệu cho API
      const thumbnailData: ThumbnailData = {
        items: items.map(item => ({
          id: item.id,
          type: item.type,
          content: item.content,
          position: item.position,
          size: item.size,
          style: item.style,
          zIndex: item.zIndex
        })),
        width: 1280,
        height: 720,
        variables 
      };

      // Gọi API để tạo thumbnail bằng ApiService
      const response = await ApiService.post<ThumbnailResponse>(
        '/images/thumbnails/generate', 
        thumbnailData
      );

      if (response.success) {
        // Cập nhật lại số dư token vì backend đã trừ token
        refreshUserToken();
          
        // Hiển thị kết quả
        setGeneratedThumbnail(`${BASE_URL}public${response.url}`);
        setShowResult(true);
          
        notifications.show({
          title: 'Thành công',
          message: 'Đã tạo thumbnail (Phí: 500 token)',
          color: 'green'
        });
      } else {
        throw new Error(response.error || 'Lỗi không xác định');
      }
    } catch (error) {
      console.error('Generation error:', error);
      notifications.show({
        title: 'Lỗi',
        message: error instanceof Error ? error.message : 'Đã xảy ra lỗi khi tạo thumbnail',
        color: 'red'
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const closeResultModal = () => {
    setShowResult(false);
  };

  return {
    isGenerating,
    generatedThumbnail,
    showResult,
    generateThumbnail,
    closeResultModal
  };
}; 